/**
 * =============================================================================
 *  EmployeeCloneService.js
 * -----------------------------------------------------------------------------
 *  Thin HTTP wrapper around the multi-profile patient clone / transfer endpoints.
 * =============================================================================
 */

import http from './http';
import { OK, CREATED } from '../utils/constant';
import { wOrgIdMiddleware } from '../utils/commonUtils';
import OrganizationService from './OrganizationService';

const EmployeeCloneService = {
  /**
   * POST /employees/{id}/clone — clone a profile into a target company.
   *
   * @param {string} url - injected by wOrgIdMiddleware
   * @param {string} employeeId
   * @param {object} payload {
   *   target_organization_id, target_company_id,
   *   clone_demographics, clone_insurance, clone_employer_forms,
   *   reason,
   * }
   */
  cloneEmployee: wOrgIdMiddleware(async (url, employeeId, payload = {}) => {
    try {
      const response = await http.POST(`${url}employees/${employeeId}/clone`, payload);

      // Success (201) → return full envelope with `data`.
      if (response && (response.code === OK || response.code === CREATED)) {
        return response.data;
      }
      // Validation / business error → forward envelope so caller can render.
      return response;
    } catch (error) {
      console.error('[EmployeeCloneService.cloneEmployee] error', error);
      if (error?.status) {
        const resData = error.data;
        if (resData?.code && resData?.data) {
          return resData;
        }
      }
      throw error;
    }
  }),

  /**
   * POST /employees/{id}/reassign — transfer profile to target company (retire source).
   */
  reassignEmployee: wOrgIdMiddleware(async (url, employeeId, payload = {}) => {
    try {
      const response = await http.POST(`${url}employees/${employeeId}/reassign`, payload);

      if (response && response.code === OK) {
        return response.data;
      }
      return response;
    } catch (error) {
      console.error('[EmployeeCloneService.reassignEmployee] error', error);
      if (error?.status) {
        const resData = error.data;
        if (resData?.code && resData?.data) {
          return resData;
        }
      }
      throw error;
    }
  }),

  /**
   * POST /employees/{id}/enable-portal — create portal login for a profile.
   *
   * @param {string} url - injected by wOrgIdMiddleware
   * @param {string} employeeId
   * @param {object} payload { portal_email }
   */
  enablePortal: wOrgIdMiddleware(async (url, employeeId, payload = {}) => {
    try {
      const response = await http.POST(`${url}employees/${employeeId}/enable-portal`, payload);

      if (response && response.code === OK) {
        return response.data;
      }
      return response;
    } catch (error) {
      console.error('[EmployeeCloneService.enablePortal] error', error);
      if (error?.status) {
        const resData = error.data;
        if (resData?.code && resData?.data) {
          return resData;
        }
      }
      throw error;
    }
  }),

  /**
   * POST /employees/check-portal-email — global portal login email availability.
   */
  checkPortalEmail: wOrgIdMiddleware(async (url, payload = {}) => {
    try {
      const response = await http.POST(`${url}employees/check-portal-email`, payload);
      if (response && response.code === OK) {
        return response.data;
      }
      return response;
    } catch (error) {
      console.error('[EmployeeCloneService.checkPortalEmail] error', error);
      return null;
    }
  }),

  /**
   * GET /employees/{id}/clone-audit — admin HIPAA review surface.
   */
  getCloneAudit: wOrgIdMiddleware(async (url, employeeId) => {
    try {
      const response = await http.GET(`${url}employees/${employeeId}/clone-audit`);
      if (response && response.code === OK) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('[EmployeeCloneService.getCloneAudit] error', error);
      return null;
    }
  }),

  /**
   * Get global clone audit logs (Configure > Clone History).
   * @param {Object} params - { orgId?, page, perPage, sort_by, sort_order, search_query, action, employee_id, from_date, to_date }
   */
  getCloneLogs: async (params = {}) => {
    try {
      const orgId = params.orgId || OrganizationService.getOrgId();
      if (!orgId) {
        console.error('getCloneLogs: Organization ID is not set');
        return null;
      }
      const { orgId: _o, search, ...rest } = params;
      const merged = { ...rest, ...(search || {}) };
      const query = Object.fromEntries(
        Object.entries(merged).filter(([, v]) => v != null && v !== '')
      );
      const queryString = new URLSearchParams(query).toString();
      const url = `organizations/${orgId}/employees/clone/logs${queryString ? `?${queryString}` : ''}`;
      const response = await http.GET(url);
      if (response.code === OK && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error getting clone logs:', error);
      return null;
    }
  },

  /**
   * POST /employees/{id}/check-target-company — clone/transfer target company guard.
   */
  checkTargetCompany: wOrgIdMiddleware(async (url, employeeId, payload = {}) => {
    try {
      const response = await http.POST(
        `${url}employees/${employeeId}/check-target-company`,
        payload
      );
      if (response && response.code === OK) {
        return response.data;
      }
      return response;
    } catch (error) {
      console.error('[EmployeeCloneService.checkTargetCompany] error', error);
      return null;
    }
  }),

  /**
   * POST /employees/duplicate-check — non-blocking duplicate detection.
   * Always returns 200 with a `blocking: false` envelope by design.
   */
  duplicateCheck: wOrgIdMiddleware(async (url, payload = {}) => {
    try {
      const response = await http.POST(`${url}employees/duplicate-check`, payload);
      if (response && response.code === OK) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('[EmployeeCloneService.duplicateCheck] error', error);
      return null;
    }
  }),
};

export default EmployeeCloneService;
